import { ChevronRight, ShieldCheck } from "lucide-react";
import { Link } from "react-router";

import { useLogout, useSession } from "@/api/queries/auth";
import { KshopLogo } from "@/components/brand/KMark";
import { Avatar, Button } from "@/components/ui";

export function WorkspacePickerPage() {
  const { data } = useSession();
  const logout = useLogout();
  if (!data?.user) return null;
  const internal = data.memberships.filter((m) => m.has_internal_access);
  const other = data.memberships.filter((m) => !m.has_internal_access);
  const last = data.last_tenant_slug;
  return (
    <div className="flex min-h-dvh flex-col items-center bg-canvas px-4 py-12">
      <main className="w-full max-w-md">
        <KshopLogo className="mb-8 justify-center" />
        <h1 className="text-xl font-semibold text-fg">Choose a workspace</h1>
        <p className="mt-0.5 mb-4 text-sm text-fg-muted">Signed in as {data.user.email}</p>
        <ul className="overflow-hidden rounded-lg border border-line bg-surface">
          {internal.map((m) => (
            <li key={m.id} className="border-b border-line last:border-b-0">
              <Link to={`/t/${m.tenant.slug}`} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-hover">
                <Avatar name={m.tenant.name} src={m.tenant.logo_url} square />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-fg">{m.tenant.name}</span>
                  {m.tenant.slug === last && <span className="text-xs text-fg-subtle">Last used</span>}
                </span>
                <ChevronRight aria-hidden className="size-4 text-fg-subtle" />
              </Link>
            </li>
          ))}
          {data.platform.capabilities.length > 0 && (
            <li>
              <Link to="/platform/tenants" className="flex items-center gap-3 px-4 py-3 hover:bg-surface-hover">
                <span className="flex size-8 items-center justify-center rounded-md bg-surface-sunken text-fg-muted">
                  <ShieldCheck aria-hidden className="size-4" />
                </span>
                <span className="flex-1 font-medium text-fg">Kasten platform administration</span>
                <ChevronRight aria-hidden className="size-4 text-fg-subtle" />
              </Link>
            </li>
          )}
        </ul>
        {other.length > 0 && (
          <p className="mt-4 text-sm text-fg-muted">
            You're also connected to {other.map((m) => m.tenant.name).join(", ")} without access to the K-SHOP application.
          </p>
        )}
        <Button variant="ghost" className="mt-6" onClick={() => logout.mutate()}>
          Sign out
        </Button>
      </main>
    </div>
  );
}

export function NoAccessPage() {
  const { data } = useSession();
  const logout = useLogout();
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4">
      <main className="w-full max-w-md text-center">
        <KshopLogo className="mb-8 justify-center" />
        <h1 className="text-xl font-semibold text-fg">No application access yet</h1>
        <p className="mt-2 text-sm text-fg-muted">
          Your account{data?.user ? ` (${data.user.email})` : ""} is connected to a company in K-SHOP, but it doesn't include access to the
          K-SHOP application. If you expected access, ask your company's K-SHOP administrator.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          {(data?.memberships.some((m) => m.has_internal_access) || (data?.platform.capabilities.length ?? 0) > 0) && (
            <Link to="/workspaces" className="inline-flex h-8 items-center rounded-md border border-line-strong bg-surface px-3 text-sm font-medium">
              Choose a workspace
            </Link>
          )}
          <Button onClick={() => logout.mutate()}>Sign out</Button>
        </div>
      </main>
    </div>
  );
}
